import type { HeadersFunction, LoaderArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import dayjs from "dayjs";
import { useLoaderData } from "@remix-run/react";
import clsx from "clsx";
import type { EventV2, MicroCMSAPIContentResponse } from "@/lib/microcms.server";
import { fetchEventListV2 } from "@/lib/microcms.server";
import { CacheRevalidateMinutes, notFound } from "@/lib/responses.server";
import common from "@/data/common.json";
import { createOGP } from "@/utilities/ogp";
import { PageContainer } from "@/components/PageContainer";
import { ArticleLayout } from "@/components/ArticleLayout";
import { DisplayText } from "@/components/DisplayText";
import { EventsHeader } from "@/components/Events/Header";
import { EventsList } from "@/components/Events/List";
import { sortEventsBySchedules } from "@/lib/microcms";
import { EventsEmpty } from "@/components/Events/Empty";

export const meta: MetaFunction<typeof loader> = () => {
  return createOGP({ path: `${common.meta.url}/events`, pageName: "イベントスケジュール一覧" });
};

export const headers: HeadersFunction = () => {
  return {
    "Cache-Control": CacheRevalidateMinutes(),
  };
};

function buildQuery(startDate: string, endDate: string) {
  return new URLSearchParams(
    `?filters=date_to_search[greater_than]${startDate}[and]date_to_search[less_than]${endDate}[and](reservation_status[contains]一般公開[or]reservation_status[contains]本予約完了[or]reservation_status[contains]仮予約中)&limit=1000&orders=date_to_search`
  ).toString();
}

export async function loader({ request }: LoaderArgs) {
  const url = new URL(request.url);
  const d = url.searchParams.get("d") || undefined;
  const regex = /^20[0-9][0-9]-(0[1-9]|1[0-2])$/;
  if (d && !regex.test(d)) {
    throw notFound();
  }

  let day = dayjs();
  if (d) {
    day = dayjs(d);
  }

  //前月の末
  const startDate = day
    .clone()
    .set("month", dayjs(d).month() - 1)
    .endOf("month")
    .format("YYYY-MM-DD");
  //来月の月初
  const endDate = day
    .clone()
    .set("month", dayjs(d).month() + 1)
    .startOf("month")
    .format("YYYY-MM-DD");
  const query = buildQuery(startDate, endDate);
  const events = await fetchEventListV2(`?${query}`);
  const displayDate = dayjs(d).format("YYYY-MM");

  return json({ events: events.contents, date: displayDate, q: query, canonical: `${common.meta.url}/events` });
}

export default function Events() {
  const { events, date } = useLoaderData<typeof loader>();

  return (
    <PageContainer>
      <ArticleLayout
        paths={[
          { name: "ホーム", url: "/" },
          { name: "イベントスケジュール一覧", url: "/events" },
        ]}
      >
        <div className={clsx("flex", "flex-col", "space-y-8")}>
          <div className="space-y-2">
            <DisplayText element="h1" size="extraLarge">
              イベントスケジュール一覧
            </DisplayText>
            <p>
              <small className="text-red-700">
                * イベント開催中は一般利用ができない場合がありますので予めご了承ください。
              </small>
            </p>
          </div>

          <EventsHeader date={date} />

          {events.length > 0 ? (
            <EventsList events={sortEventsBySchedules(events) as MicroCMSAPIContentResponse<EventV2>[]} />
          ) : (
            <EventsEmpty />
          )}
        </div>
      </ArticleLayout>
    </PageContainer>
  );
}
